import React from "react";

/**
 * Restricted production panel for Frame 002. The debug control list
 * is exactly the one specified in the production instructions —
 * ten parameters, one Play, one Stop, screenshot, save/load
 * presentation preset.
 *
 * No new visual systems until Tuesday.
 */

const EASINGS = ["smoothstep", "linear", "cubic"];

export default function Frame002Panel({
  params,
  onParamChange,
  onPlay,
  onStop,
  onJumpToEnd,
  onScreenshot,
  onSavePresentationPreset,
  onLoadPresentationPreset,
  onHideUI,
  progress,
  hasPresentationPreset,
}) {
  return (
    <aside
      data-testid="frame002-panel"
      style={panelStyle}
    >
      <header style={headerStyle}>
        <span style={titleStyle}>Frame 002 · Production</span>
        <button
          onClick={onHideUI}
          data-testid="btn-hide-ui"
          style={ghostButtonStyle}
          title="Hide UI (H)"
        >
          hide (H)
        </button>
      </header>

      <p style={introStyle}>
        Four acts in order: withdraw · hold · rotate · settle.
        Perspective reveals that a relationship exists; rotation
        reveals how it is constructed. Objects never move.
      </p>

      {/* Playback */}
      <SectionHeader title="Playback" />
      <div style={playbackRowStyle}>
        <ActionButton onClick={onPlay} testId="btn-play" primary>
          {(progress?.state === "idle") ? "play" : "restart"}
        </ActionButton>
        <ActionButton onClick={onStop} testId="btn-stop">
          stop
        </ActionButton>
        <ActionButton onClick={onJumpToEnd} testId="btn-jump-end">
          end
        </ActionButton>
      </div>
      <PlaybackReadout progress={progress} params={params} />

      {/* Camera */}
      <SectionHeader title="Camera" />
      <Slider
        keyName="cameraStartZ"
        label="Start position"
        note="Camera Z at t=0. Object A dominates the frame."
        value={params.cameraStartZ}
        min={0.20}
        max={1.50}
        step={0.005}
        onChange={(v) => onParamChange("cameraStartZ", v)}
      />
      <Slider
        keyName="cameraEndZ"
        label="End position"
        note="Camera Z at t=1. Relationship is legible; camera stops here."
        value={params.cameraEndZ}
        min={1.20}
        max={6.00}
        step={0.01}
        onChange={(v) => onParamChange("cameraEndZ", v)}
      />
      <Slider
        keyName="cameraDuration"
        label="Duration"
        note="Seconds for the one-way journey."
        value={params.cameraDuration}
        min={3.0}
        max={30.0}
        step={0.1}
        unit="s"
        onChange={(v) => onParamChange("cameraDuration", v)}
      />
      <div style={{ marginBottom: 10 }}>
        <div style={rowStyle}>
          <label style={labelStyle}>Easing</label>
          <span style={valueStyle}>{params.easing}</span>
        </div>
        <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
          {EASINGS.map((e) => (
            <ModeButton
              key={e}
              active={params.easing === e}
              onClick={() => onParamChange("easing", e)}
              testId={`easing-${e}`}
            >
              {e}
            </ModeButton>
          ))}
        </div>
      </div>
      <Slider
        keyName="distanceHoldDuration"
        label="Distance hold"
        note="Brief hold before rotation begins. Lets the observer feel the gap."
        value={params.distanceHoldDuration}
        min={0.0}
        max={10.0}
        step={0.1}
        unit="s"
        onChange={(v) => onParamChange("distanceHoldDuration", v)}
      />

      {/* Rotation — inspection, not orbit. */}
      <SectionHeader title="Rotation" />
      <p style={{ ...noteStyle, marginBottom: 6, lineHeight: 1.6 }}>
        Perspective reveals that a relationship exists;
        rotation reveals how the relationship is constructed.
      </p>
      <Slider
        keyName="rotationArc"
        label="Arc"
        note="Radians. Keep small — jeweler, not turntable."
        value={params.rotationArc}
        min={0.0}
        max={0.6}
        step={0.005}
        unit="rad"
        onChange={(v) => onParamChange("rotationArc", v)}
      />
      <Slider
        keyName="rotationDuration"
        label="Rotation duration"
        note="Seconds for the small arc. Deliberate, restrained."
        value={params.rotationDuration}
        min={1.0}
        max={15.0}
        step={0.1}
        unit="s"
        onChange={(v) => onParamChange("rotationDuration", v)}
      />
      <Slider
        keyName="holdDuration"
        label="Final hold"
        note="Seconds the camera stays after rotation completes."
        value={params.holdDuration}
        min={0.0}
        max={20.0}
        step={0.1}
        unit="s"
        onChange={(v) => onParamChange("holdDuration", v)}
      />

      {/* Objects */}
      <SectionHeader title="Objects" />
      <Slider
        keyName="objectSeparation"
        label="Separation"
        note="Global scale of the arrangement. Protect the distance."
        value={params.objectSeparation}
        min={0.4}
        max={2.2}
        step={0.01}
        onChange={(v) => onParamChange("objectSeparation", v)}
      />
      <Slider
        keyName="objectScale"
        label="Scale"
        note="Physical size of each object."
        value={params.objectScale}
        min={0.04}
        max={0.30}
        step={0.001}
        onChange={(v) => onParamChange("objectScale", v)}
      />

      {/* Light */}
      <SectionHeader title="Light" />
      <Slider
        keyName="lightIntensity"
        label="Intensity"
        note="Emissive brightness of the core."
        value={params.lightIntensity}
        min={0.1}
        max={4.0}
        step={0.01}
        onChange={(v) => onParamChange("lightIntensity", v)}
      />
      <Slider
        keyName="lightFalloff"
        label="Falloff"
        note="Halo radius. Keep small. Never atmospheric."
        value={params.lightFalloff}
        min={0.0}
        max={0.5}
        step={0.005}
        onChange={(v) => onParamChange("lightFalloff", v)}
      />
      <Slider
        keyName="materialTransmission"
        label="Material transmission"
        note="Transmission through the frosted shell."
        value={params.materialTransmission}
        min={0.0}
        max={1.0}
        step={0.005}
        onChange={(v) => onParamChange("materialTransmission", v)}
      />

      {/* Presentation preset */}
      <SectionHeader title="Presentation preset" />
      <div style={{ display: "grid", gap: 6, marginTop: 6 }}>
        <ActionButton
          onClick={onSavePresentationPreset}
          testId="btn-save-presentation"
        >
          save as presentation preset
        </ActionButton>
        <ActionButton
          onClick={onLoadPresentationPreset}
          testId="btn-load-presentation"
          disabled={!hasPresentationPreset}
        >
          {hasPresentationPreset ? "load presentation preset" : "(no preset saved)"}
        </ActionButton>
        <ActionButton onClick={onScreenshot} testId="btn-screenshot">
          screenshot + metadata
        </ActionButton>
      </div>

      <footer style={footerStyle}>
        Frame 002 is complete when an observer can truthfully say:
        <br />
        <em>"Nothing moved, but my understanding of the distance changed."</em>
      </footer>
    </aside>
  );
}


function PlaybackReadout({ progress, params }) {
  const state = progress?.state || "idle";
  const phaseT = progress?.phaseT ?? 0;
  const phaseDur = progress?.phaseDuration ?? 0;
  const phaseFrac = progress?.phaseFraction ?? 0;

  // Overall progress across the four acts, weighted by duration.
  const totalDur =
    (params.cameraDuration || 0) +
    (params.distanceHoldDuration || 0) +
    (params.rotationDuration || 0) +
    (params.holdDuration || 0);
  const elapsedBefore = {
    "idle": 0,
    "withdrawing": 0,
    "holding-distance": params.cameraDuration,
    "rotating": params.cameraDuration + params.distanceHoldDuration,
    "settling":
      params.cameraDuration +
      params.distanceHoldDuration +
      params.rotationDuration,
    "done": totalDur,
  }[state] || 0;
  const overallFrac = totalDur > 0 ? Math.min(1, (elapsedBefore + (state === "done" ? 0 : phaseT)) / totalDur) : (state === "done" ? 1 : 0);

  const phaseLabel = {
    "idle": "idle",
    "withdrawing": "act 1 · withdraw",
    "holding-distance": "act 2 · distance",
    "rotating": "act 3 · rotate",
    "settling": "act 4 · settle",
    "done": "done",
  }[state] || state;

  const phaseRemaining = Math.max(0, phaseDur - phaseT);

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={rowStyle}>
        <span style={labelStyle}>Phase</span>
        <span style={{ ...valueStyle, color: "#d9b98a" }}>{phaseLabel}</span>
      </div>
      <div style={{ height: 3, background: "rgba(255,255,255,0.05)", borderRadius: 2, overflow: "hidden", marginTop: 4 }}>
        <div style={{ width: `${Math.round(phaseFrac * 100)}%`, height: "100%", background: "#d9b98a", opacity: 0.75, transition: "width 0.1s linear" }} />
      </div>
      <div style={{ ...rowStyle, marginTop: 4 }}>
        <span style={{ ...labelStyle, fontSize: 9 }}>overall</span>
        <span style={{ ...valueStyle, fontSize: 9 }}>
          {state === "done" || state === "idle"
            ? state
            : `${phaseRemaining.toFixed(1)}s in phase`}
        </span>
      </div>
      <div style={{ height: 2, background: "rgba(255,255,255,0.04)", borderRadius: 2, overflow: "hidden", marginTop: 2 }}>
        <div style={{ width: `${Math.round(overallFrac * 100)}%`, height: "100%", background: "#78716c", opacity: 0.6, transition: "width 0.1s linear" }} />
      </div>
    </div>
  );
}


function SectionHeader({ title }) {
  return (
    <div style={sectionHeaderStyle}>{title}</div>
  );
}


function Slider({ keyName, label, note, value, min, max, step, unit, onChange }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={rowStyle}>
        <label style={labelStyle}>{label}</label>
        <span style={valueStyle}>
          {value.toFixed(step < 1 ? 3 : 0)}
          {unit ? ` ${unit}` : ""}
        </span>
      </div>
      {note && <div style={noteStyle}>{note}</div>}
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        data-testid={`control-${keyName}`}
        style={{ width: "100%", accentColor: "#d9b98a", cursor: "pointer" }}
      />
    </div>
  );
}


function ActionButton({ children, onClick, testId, primary, disabled }) {
  return (
    <button
      onClick={disabled ? undefined : onClick}
      data-testid={testId}
      disabled={disabled}
      style={{
        background: disabled
          ? "rgba(255,255,255,0.02)"
          : primary
          ? "rgba(217,185,138,0.14)"
          : "rgba(255,255,255,0.03)",
        border: primary
          ? "1px solid rgba(217,185,138,0.5)"
          : "1px solid rgba(255,255,255,0.12)",
        color: disabled
          ? "#57534e"
          : primary
          ? "#f3ead6"
          : "#d6d3d1",
        fontFamily: "inherit",
        fontSize: 10,
        padding: "6px 10px",
        borderRadius: 3,
        cursor: disabled ? "not-allowed" : "pointer",
        letterSpacing: "0.08em",
        textAlign: "left",
        opacity: disabled ? 0.6 : 1,
      }}
      onMouseEnter={(e) => {
        if (disabled || primary) return;
        e.currentTarget.style.background = "rgba(255,255,255,0.08)";
      }}
      onMouseLeave={(e) => {
        if (disabled || primary) return;
        e.currentTarget.style.background = "rgba(255,255,255,0.03)";
      }}
    >
      {children}
    </button>
  );
}


function ModeButton({ children, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      style={{
        flex: 1,
        background: active ? "rgba(217,185,138,0.14)" : "rgba(255,255,255,0.03)",
        border: `1px solid ${active ? "rgba(217,185,138,0.5)" : "rgba(255,255,255,0.12)"}`,
        color: active ? "#f3ead6" : "#a8a29e",
        fontFamily: "inherit",
        fontSize: 10,
        padding: "4px 8px",
        borderRadius: 3,
        cursor: "pointer",
        letterSpacing: "0.05em",
      }}
    >
      {children}
    </button>
  );
}


const panelStyle = {
  position: "fixed",
  top: 16,
  right: 16,
  bottom: 16,
  width: 320,
  overflowY: "auto",
  background: "rgba(6,6,10,0.82)",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 6,
  padding: "16px 14px 20px",
  color: "#d6d3d1",
  fontFamily: "ui-monospace, SFMono-Regular, monospace",
  fontSize: 11,
  lineHeight: 1.5,
  zIndex: 10,
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  marginBottom: 14,
  paddingBottom: 10,
  borderBottom: "1px solid rgba(255,255,255,0.08)",
};

const titleStyle = {
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#a8a29e",
  fontSize: 10,
};

const introStyle = {
  margin: "0 0 14px",
  color: "#a8a29e",
  fontSize: 10,
  lineHeight: 1.6,
  fontStyle: "italic",
};

const sectionHeaderStyle = {
  color: "#a8a29e",
  fontSize: 10,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  marginTop: 8,
  marginBottom: 8,
};

const rowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
};

const labelStyle = {
  color: "#d6d3d1",
  fontSize: 10,
};

const valueStyle = {
  color: "#78716c",
  fontSize: 10,
  fontFamily: "ui-monospace, SFMono-Regular, monospace",
};

const noteStyle = {
  color: "#57534e",
  fontSize: 9,
  fontStyle: "italic",
  marginTop: 1,
  marginBottom: 3,
};

const playbackRowStyle = {
  display: "grid",
  gridTemplateColumns: "1.5fr 1fr 1fr",
  gap: 6,
  marginBottom: 10,
};

const ghostButtonStyle = {
  background: "transparent",
  border: "1px solid rgba(255,255,255,0.15)",
  color: "#a8a29e",
  fontFamily: "inherit",
  fontSize: 10,
  padding: "2px 8px",
  borderRadius: 3,
  cursor: "pointer",
  letterSpacing: "0.08em",
};

const footerStyle = {
  marginTop: 20,
  paddingTop: 10,
  borderTop: "1px solid rgba(255,255,255,0.06)",
  color: "#57534e",
  fontStyle: "italic",
  fontSize: 10,
  lineHeight: 1.6,
};
